"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { usePathname } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import {
  Menu, X, Phone, ChevronDown, Linkedin, Facebook, Instagram,
  Eye, Brain, Puzzle, HeartHandshake, HeartPulse, Scale, Users, Crown, ArrowRight, GraduationCap,
} from "lucide-react"

// ── Data ─────────────────────────────────────────────────────────────────────

const servicesLinks = [
  { label: "EMDR Therapy",        href: "/emdr-therapy",                image: "/images/services/emdr-therapy-session.jpg",      icon: Eye },
  { label: "EMDR Intensive",      href: "/emdr-intensive",              image: "/images/services/emdr-intensive-session.jpg",    icon: Brain },
  { label: "ADHD and Autism",     href: "/adhd-autism-assessments",     image: "/images/services/adhd-autism-571630.jpg",       icon: Puzzle },
  { label: "Trauma Therapy",      href: "/trauma-therapy",              image: "/images/services/trauma-therapy-session.jpg",    icon: HeartHandshake },
  { label: "Individual Therapy",  href: "/individual-therapy",          image: "/images/services/individual-therapy-session.jpg",icon: HeartPulse },
  { label: "DWI Services",        href: "/substance-abuse-therapy",     image: "/images/services/dwi-substance-session.jpg",     icon: Scale },
  { label: "Couples Intensive",   href: "/couples-intensive-therapy",   image: "/images/services/couples-conflict-session.jpg",  icon: Users },
  { label: "Concierge Program",   href: "/concierge-membership-program",image: "/images/services/concierge-office-session.jpg",  icon: Crown },
  { label: "Clinical Supervision", href: "/services/supervision",        image: "/images/services/supervision-hero-775909.jpg",  icon: GraduationCap },
]

const aboutLinks = [
  { label: "Insurance Accepted", href: "/insurance" },
  { label: "Meet the Team",      href: "/team" },
]

const mainLinks = [
  { label: "Gallery",    href: "/gallery" },
  { label: "Blog",       href: "/blog" },
  { label: "Contact Us", href: "/contact" },
]

const socialLinks = [
  { href: "https://www.psychologytoday.com/us/therapists/william-anthony-burchette-greensboro-nc/900165", label: "Psychology Today", icon: "pt" },
  { href: "https://www.linkedin.com/in/anthonyburchette/",          label: "LinkedIn",        icon: "linkedin" },
  { href: "https://www.facebook.com/BattlefieldCounselingNC/",      label: "Facebook",        icon: "facebook" },
  { href: "https://www.instagram.com/battlefieldcounselingnc/",     label: "Instagram",       icon: "instagram" },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

const PtBadge = ({ size = 20 }: { size?: number }) => (
  <span
    style={{ width: size, height: size, fontSize: size * 0.44, fontFamily: "'Anton', sans-serif" }}
    className="inline-flex items-center justify-center rounded-full bg-white text-black leading-none flex-shrink-0"
  >
    PT
  </span>
)

type IconProps = { icon: string; light?: boolean; size?: number }
const SocialIcon = ({ icon, light = false, size = 18 }: IconProps) => {
  const cls = `transition-colors ${light ? "text-white/70 hover:text-white" : "text-foreground/55 hover:text-[hsl(var(--terracotta))]"}`
  if (icon === "pt")        return <PtBadge size={size} />
  if (icon === "linkedin")  return <Linkedin  style={{ width: size, height: size }} className={cls} />
  if (icon === "facebook")  return <Facebook  style={{ width: size, height: size }} className={cls} />
  if (icon === "instagram") return <Instagram style={{ width: size, height: size }} className={cls} />
  return null
}

// ── Simple Dropdown Panel (About Us) ─────────────────────────────────────────

const DropdownPanel = ({ items }: { items: { label: string; href: string }[] }) => (
  <motion.div
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.15 }}
    className="absolute top-full left-0 pt-3 z-50"
  >
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl shadow-xl py-2 min-w-[210px]">
      {items.map((item) => (
        <a
          key={item.label}
          href={item.href}
          className="block px-4 py-2.5 text-sm text-foreground hover:bg-[hsl(var(--sage-light))]/50 hover:text-[hsl(var(--terracotta))] transition-colors"
        >
          {item.label}
        </a>
      ))}
    </div>
  </motion.div>
)

// ── Services Mega Menu ────────────────────────────────────────────────────────

const ServicesMegaMenu = ({
  onMouseEnter,
  onMouseLeave,
}: {
  onMouseEnter: () => void
  onMouseLeave: () => void
}) => {
  const [activeLabel, setActiveLabel] = useState(servicesLinks[0].label)
  const active = servicesLinks.find((s) => s.label === activeLabel) ?? servicesLinks[0]

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="pt-3 z-50"
      style={{ position: "fixed", top: "80px", left: "calc(50vw - 450px)" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className="bg-white border border-[hsl(var(--border))] rounded-2xl shadow-2xl overflow-hidden flex"
        style={{ width: "900px" }}
      >
        {/* Left — 2 × 4 service grid */}
        <div className="flex-1 p-4 grid grid-cols-2 gap-1 content-start">
          {servicesLinks.map((link) => {
            const Icon = link.icon
            const isActive = link.label === activeLabel
            return (
              <a
                key={link.label}
                href={link.href}
                onMouseEnter={() => setActiveLabel(link.label)}
                className={`flex items-center gap-3 rounded-xl px-3 py-3 transition-all ${
                  isActive
                    ? "bg-[hsl(var(--sage-light))]/70 text-[hsl(var(--sage-dark))]"
                    : "hover:bg-[hsl(var(--sage-light))]/40 text-foreground"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isActive
                      ? "bg-[hsl(var(--sage-dark))] text-white"
                      : "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-dark))]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium leading-snug">{link.label}</span>
              </a>
            )
          })}
        </div>

        {/* Right — Animated image preview */}
        <div className="w-[400px] flex-shrink-0 relative bg-[hsl(var(--sage-dark))]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeLabel}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="absolute inset-0"
            >
              <img
                src={active.image}
                alt={active.label}
                className="w-full h-full object-cover"
                width={400}
                height={400}
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222_48%_12%/0.85)] via-[hsl(222_48%_12%/0.2)] to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white/60 text-[10px] font-semibold tracking-[0.15em] uppercase mb-1">
                  Our Services
                </p>
                <p className="text-white text-sm font-semibold leading-snug mb-3">
                  {active.label}
                </p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[hsl(var(--terracotta))] text-xs font-semibold tracking-wide">
                    Learn More
                  </span>
                  <ArrowRight className="w-3 h-3 text-[hsl(var(--terracotta))]" />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

// ── Navbar ────────────────────────────────────────────────────────────────────

const Navbar = () => {
  const pathname = usePathname()
  const isHome = pathname === "/"

  const [scrolled,       setScrolled]       = useState(false)
  const [mobileOpen,     setMobileOpen]     = useState(false)
  const [openDropdown,   setOpenDropdown]   = useState<string | null>(null)
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openDrop = useCallback((name: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpenDropdown(name)
  }, [])

  const closeDrop = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 300)
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // On internal pages always show the "scrolled" (white) state
  const isScrolled = scrolled || !isHome

  const linkCls = `text-sm font-medium transition-colors duration-200 hover:text-[hsl(var(--terracotta))] ${
    isScrolled ? "text-foreground" : "text-white drop-shadow"
  }`

  // Active link highlight — turns the current page's nav link to the same
  // blue/terracotta accent used on the CTA button (hsl(207 72% 52%))
  const activeLinkCls = `text-sm font-semibold text-[hsl(var(--terracotta))] transition-colors duration-200 ${
    isScrolled ? "" : "drop-shadow"
  }`

  // Dropdown "parent" labels (Services, About Us) go blue when the user
  // is on any page under that group of routes
  const isOnServicesPage = servicesLinks.some((l) => pathname === l.href || pathname.startsWith(l.href + "/"))
  const isOnAboutPage    = aboutLinks.some((l)    => pathname === l.href || pathname.startsWith(l.href + "/"))
  const isActiveMainLink = (href: string) => pathname === href || pathname.startsWith(href + "/")

  const toggleMobile = (key: string) =>
    setMobileExpanded((v) => (v === key ? null : key))

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isScrolled
          ? "bg-[hsl(var(--cream))]/80 backdrop-blur-md shadow-sm border-b border-[hsl(var(--border))]"
          : "bg-black/30 backdrop-blur-sm"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 md:h-20 grid grid-cols-[auto_1fr_auto] items-center gap-4 py-4">

        {/* ── Logo / Brand ────────────────────────────────────────── */}
        <a href="/" className="flex items-center gap-3 flex-shrink-0">
          <div className="relative h-10 md:h-12 flex-shrink-0" style={{ minWidth: "40px" }}>
            <img
              src="/images/logo-white.png"
              alt="Battlefield Counseling"
              className="h-10 md:h-12 w-auto object-contain absolute inset-0"
              style={{ opacity: isScrolled ? 0 : 1, transition: "opacity 400ms ease" }}
              width={120}
              height={48}
              fetchPriority="high"
              decoding="async"
            />
            <img
              src="/images/logo-blue.png"
              alt=""
              aria-hidden="true"
              className="h-10 md:h-12 w-auto object-contain absolute inset-0"
              style={{ opacity: isScrolled ? 1 : 0, transition: "opacity 400ms ease" }}
              width={120}
              height={48}
              fetchPriority="high"
              decoding="async"
            />
            {/* Invisible spacer reserves width */}
            <img src="/images/logo-blue.png" alt="" aria-hidden="true" className="h-10 md:h-12 w-auto object-contain invisible" width={120} height={48} />
          </div>
          <div className="hidden sm:flex flex-col leading-snug">
            <span className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-300 ${isScrolled ? "text-[hsl(var(--sage-dark))]" : "text-white drop-shadow"}`}>
              Battlefield Counseling
            </span>
            <span className={`text-[10px] font-medium tracking-[0.18em] uppercase transition-colors duration-300 ${isScrolled ? "text-[hsl(var(--slate))]" : "text-white/75 drop-shadow"}`}>
              LLC · North Carolina
            </span>
          </div>
        </a>

        {/* ── Desktop Nav Links ────────────────────────────────────── */}
        <div className="hidden lg:flex items-center justify-center gap-5">

          {/* Services dropdown */}
          <div onMouseEnter={() => openDrop("services")} onMouseLeave={closeDrop}>
            <button className={`${isOnServicesPage ? activeLinkCls : linkCls} flex items-center gap-1`}>
              Services
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${openDropdown === "services" ? "rotate-180" : ""}`} />
            </button>
          </div>

          {/* About Us dropdown */}
          <div className="relative" onMouseEnter={() => openDrop("about")} onMouseLeave={closeDrop}>
            <button className={`${isOnAboutPage ? activeLinkCls : linkCls} flex items-center gap-1`}>
              About Us
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${openDropdown === "about" ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {openDropdown === "about" && <DropdownPanel items={aboutLinks} />}
            </AnimatePresence>
          </div>

          {/* Standard links */}
          {mainLinks.map((link) => (
            <a key={link.label} href={link.href} className={isActiveMainLink(link.href) ? activeLinkCls : linkCls}>
              {link.label}
            </a>
          ))}
        </div>

        {/* ── Right area ───────────────────────────────────────────── */}
        <div className="flex items-center gap-3 justify-end">

          {/* Desktop: Phone + Social + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:3363105098"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 hover:text-[hsl(var(--terracotta))] ${isScrolled ? "text-foreground" : "text-white drop-shadow"}`}
            >
              <Phone className="w-3.5 h-3.5" />
              336.310.5098
            </a>

            <div className={`w-px h-4 ${isScrolled ? "bg-foreground/20" : "bg-white/30"}`} />

            {/* Social icons */}
            <div className="flex items-center gap-2.5">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="hover:opacity-75 transition-opacity"
                >
                  <SocialIcon icon={s.icon} light={!isScrolled} size={18} />
                </a>
              ))}
            </div>

            <div className={`w-px h-4 ${isScrolled ? "bg-foreground/20" : "bg-white/30"}`} />

            <a
              href="/book-a-session"
              className="inline-flex items-center justify-center rounded-full bg-[hsl(var(--terracotta))] text-white px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-[hsl(var(--terracotta))]/90 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 whitespace-nowrap"
            >
              Book A Session
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${isScrolled ? "text-foreground" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ── Services Megamenu — positioned relative to full-width nav ── */}
      <AnimatePresence>
        {openDropdown === "services" && (
          <ServicesMegaMenu
            onMouseEnter={() => openDrop("services")}
            onMouseLeave={closeDrop}
          />
        )}
      </AnimatePresence>

      {/* ── Mobile Menu ───────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            className="lg:hidden bg-[hsl(var(--sage-dark))] backdrop-blur-md border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-5 flex flex-col gap-0.5">

              {/* Services expandable */}
              <div>
                <button
                  onClick={() => toggleMobile("services")}
                  className={`flex items-center justify-between w-full text-base font-medium hover:text-[hsl(var(--terracotta))] py-2.5 border-b border-white/10 transition-colors ${isOnServicesPage ? "text-[hsl(var(--terracotta))]" : "text-white"}`}
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === "services" ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileExpanded === "services" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden pl-3 pt-1 pb-1"
                    >
                      {servicesLinks.map((link) => (
                        <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-white/75 hover:text-[hsl(var(--terracotta))] transition-colors">
                          {link.label}
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* About Us expandable */}
              <div>
                <button
                  onClick={() => toggleMobile("about")}
                  className={`flex items-center justify-between w-full text-base font-medium hover:text-[hsl(var(--terracotta))] py-2.5 border-b border-white/10 transition-colors ${isOnAboutPage ? "text-[hsl(var(--terracotta))]" : "text-white"}`}
                >
                  About Us
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === "about" ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileExpanded === "about" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden pl-3 pt-1 pb-1"
                    >
                      {aboutLinks.map((link) => (
                        <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-white/75 hover:text-[hsl(var(--terracotta))] transition-colors">
                          {link.label}
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Standard links */}
              {mainLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)}
                  className={`text-base font-medium hover:text-[hsl(var(--terracotta))] py-2.5 border-b border-white/10 transition-colors ${isActiveMainLink(link.href) ? "text-[hsl(var(--terracotta))]" : "text-white"}`}>
                  {link.label}
                </a>
              ))}

              {/* Phone */}
              <a href="tel:3363105098"
                className="flex items-center gap-2 text-base font-medium text-white hover:text-[hsl(var(--terracotta))] py-2.5 transition-colors">
                <Phone className="w-4 h-4" /> 336.310.5098
              </a>

              {/* Social icons */}
              <div className="flex items-center gap-4 py-3">
                {socialLinks.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    className="hover:opacity-70 transition-opacity">
                    <SocialIcon icon={s.icon} light={true} size={22} />
                  </a>
                ))}
              </div>

              {/* CTA */}
              <a href="/book-a-session" onClick={() => setMobileOpen(false)}
                className="mt-1 inline-flex items-center justify-center rounded-full bg-[hsl(var(--terracotta))] text-white px-6 py-3 text-sm font-semibold tracking-wide">
                Book A Session
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar
